import React, { useEffect, useState } from 'react'
import moment from 'moment-timezone'
import Grid from '@mui/material/Grid'
import DateRangePicker from '../../../common/DateRangePicker'
import withTranslationWrapper from '../../../../HOC/HocTranslate'
import { notifyError } from '../../../../Utils/CustomNotifcations'
import { apiFetch } from '../../../../Utils/apiFetch'

function splitUsageValue (value) {
  const match = String(value || '')
    .trim()
    .match(/^([\d.,]+)\s*(.+)$/)
  if (!match) return null
  return { amount: match[1], unit: match[2] }
}

function getInitialRange () {
  const from = moment().subtract(30, 'days').startOf('day')
  const to = moment().startOf('day')
  return {
    from,
    to,
    selectedDate: from.format('YYYY-MM-DD') + ' - ' + to.format('YYYY-MM-DD')
  }
}

function getTimezone (logInUser, ServerSetting) {
  let timezone = 'Asia/Riyadh'
  if (ServerSetting?.attributes?.timezone) {
    timezone = ServerSetting.attributes.timezone
  }
  if (logInUser?.attributes?.timezone) {
    timezone = logInUser.attributes.timezone
  }
  return timezone
}

function toAccountDayIso (date, edge, timezone) {
  const ymd = moment(date).format('YYYY-MM-DD')
  const zoned = moment.tz(ymd, 'YYYY-MM-DD', timezone)
  return (edge === 'start' ? zoned.startOf('day') : zoned.endOf('day')).toISOString()
}

function DeviceDataUsage ({
  deviceId,
  logInUser,
  ServerSetting,
  dispatch,
  translate
}) {
  const [range, setRange] = useState(getInitialRange)
  const [usage, setUsage] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  // Mount + deviceId change + applied date-range change
  useEffect(() => {
    const { from, to } = range
    if (!deviceId || !from || !to) return

    const timezone = getTimezone(logInUser, ServerSetting)
    const fromDate = toAccountDayIso(from, 'start', timezone)
    const toDate = toAccountDayIso(to, 'end', timezone)

    let cancelled = false
    setLoading(true)
    setError(false)

    apiFetch(
      `/api/devices/dataUsage?deviceId=${deviceId}&fromDate=${encodeURIComponent(
        fromDate
      )}&toDate=${encodeURIComponent(toDate)}`
    )
      .then(response => {
        if (!response.ok) throw response
        return response.json().then(payload => {
          if (cancelled) return
          if (payload?.statusCode === '440') {
            window.location.replace('/login')
            return
          }
          let data = ''
          if (payload && typeof payload === 'object' && 'data' in payload) {
            data = String(payload.data ?? '')
          } else {
            data =
              typeof payload === 'string' ? payload : String(payload ?? '')
          }
          setUsage(data)
          setLoading(false)
          setError(false)
        })
      })
      .catch(() => {
        if (cancelled) return
        setUsage('')
        setLoading(false)
        setError(true)
        if (dispatch) {
          dispatch(notifyError('failedToLoadDeviceDataUsage'))
        }
      })

    return () => {
      cancelled = true
    }
    // Intentionally keyed like the class: fetch on mount, deviceId, or applied range — not on every parent re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, range.selectedDate])

  const showDates = (e, picker) => {
    if (!picker?.startDate || !picker?.endDate) return

    const from = picker.startDate.clone()
    const to = picker.endDate.clone()
    const nextSelected =
      moment(from).format('YYYY-MM-DD') +
      ' - ' +
      moment(to).format('YYYY-MM-DD')

    // Same calendar days → same API params; skip refetch
    if (range.selectedDate === nextSelected) return

    setRange({ from, to, selectedDate: nextSelected })
  }

  const displayValue = error ? '—' : usage?.trim() || '—'
  const parts = displayValue === '—' ? null : splitUsageValue(displayValue)

  return (
    <Grid container spacing={2} style={{ marginTop: 4, marginBottom: 8 }}>
      <Grid item md={3} sm={6} xs={12}>
        <div
          style={{
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: 6,
            padding: '12px 14px',
            background: 'rgba(0,0,0,0.03)'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 8
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#666'
              }}
            >
              {translate('deviceDataUsage')}
            </span>
            <span
              dir='ltr'
              style={{
                opacity: loading ? 0.6 : 1,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'flex-end'
              }}
            >
              {loading && !usage ? (
                <span style={{ fontSize: 14, color: '#999' }}>…</span>
              ) : parts ? (
                <>
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      lineHeight: 1,
                      color: '#222'
                    }}
                  >
                    {parts.amount}
                  </span>
                  <span
                    style={{
                      marginLeft: 4,
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#666'
                    }}
                  >
                    {parts.unit}
                  </span>
                </>
              ) : (
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    lineHeight: 1,
                    color: '#222'
                  }}
                >
                  {displayValue}
                </span>
              )}
            </span>
          </div>
          <DateRangePicker
            selectedDate={range.selectedDate}
            label={translate('selectDate')}
            onApply={showDates}
            startDate={range.from}
            endDate={range.to}
            alwaysShowCalendars
          />
        </div>
      </Grid>
    </Grid>
  )
}

export default withTranslationWrapper(DeviceDataUsage)
